import { useState } from 'react';
import ExitToApp from '@mui/icons-material/ExitToApp';
import Edit from '@mui/icons-material/Edit';
import Share from '@mui/icons-material/Share';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PersonIcon from '@mui/icons-material/Person';
import {
  AppBar,
  Avatar,
  Button,
  ButtonBase,
  IconButton,
  Menu,
  MenuItem,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { GoogleLogin } from 'components/login/GoogleLogin';
import { ReactComponent as BoweryLogo } from 'images/boweryHeaderWhite.svg';
import { observer } from 'mobx-react-lite';
import { useFeatureFlag } from '@bowery-valuation/feature-flagger-client';
import { featureFlags } from 'utils/config';
import { useRootStore } from 'stores/RootStore';
import { PropertyUnderwritingPages, UnderwritingRouteName, RouteName } from 'stores/RouteStore';

const Toolbar = styled('div')(({ theme }) => ({
  padding: '8px 16px',
  [theme.breakpoints.down('sm')]: {
    padding: '8px 0px',
  },
  display: 'flex',
  alignItems: 'center',
  color: '#FFFFFF',
}));

const LinkButton: React.FC<{
  children: React.ReactNode;
  variant?: 'text' | 'outlined' | 'contained';
  onClick: any;
  sx?: object;
  isActive?: boolean;
}> = ({ children, variant = 'text', sx = {}, isActive, onClick, ...props }) => (
  <Button
    variant={variant}
    onClick={onClick}
    startIcon={isActive ? <ArrowForwardIosIcon sx={{ fontSize: '16px  !important', color: '#FFFFFF' }} /> : undefined}
    sx={{
      marginRight: 2,
      textTransform: 'none',
      fontSize: '18px',
      position: 'relative',
      fontWeight: isActive ? 700 : 400,
      color: '#FFFFFF',
      lineHeight: '24px',
      ...sx,
    }}
    {...props}
  >
    {children}
  </Button>
);

type HeaderProps = {
  onBack?: () => void;
};

const isUnderwritingRoute = (route?: RouteName): route is UnderwritingRouteName => {
  return PropertyUnderwritingPages.includes(route as UnderwritingRouteName);
};

const Header = observer(({ onBack }: HeaderProps) => {
  const { authStore, routeStore, diligencePageStore } = useRootStore();
  const { route, navigateByName } = routeStore;
  const { status, logout, isLoggedIn, isAdmin, isInternal, currentUser, getFeatureFlagValueForUser } = authStore;
  const { diligenceStore } = diligencePageStore;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onEditDiligenceClick = diligenceStore?.showAdminModal;
  const onShareDiligenceClick = diligenceStore?.showShareModal;

  const theme = useTheme();
  const mdScreen = useMediaQuery(theme.breakpoints.down('md'));
  const canEditDiligence = route === 'diligence' && isInternal && onEditDiligenceClick;
  const canShareDiligence = route === 'diligence' && isInternal && onShareDiligenceClick;
  const isLoggedOut = status === 'invalid';

  const userRegistration = useFeatureFlag(featureFlags.userRegistration);
  const showNewClientTech = getFeatureFlagValueForUser(featureFlags.showNewClientTech);

  const logo = <BoweryLogo title="Bowery logo" style={{ marginLeft: 0, marginRight: 30, height: '100%' }} />;

  return (
    <AppBar position="static" sx={{ color: '#4260D3' }} elevation={1}>
      <Toolbar aria-label="header">
        <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
          {onBack ? <ButtonBase onClick={() => onBack()}>{logo}</ButtonBase> : logo}
          {isLoggedIn && (
            <>
              <LinkButton
                aria-label="account"
                onClick={() => navigateByName('jobs')}
                isActive={route === 'jobs' || route === 'diligence'}
              >
                Appraisals
              </LinkButton>
              {showNewClientTech && (
                <>
                  <LinkButton
                    aria-label="account"
                    onClick={() => navigateByName('underwritingDashboard')}
                    isActive={route && (route === 'underwritingDashboard' || isUnderwritingRoute(route))}
                  >
                    Underwriting
                  </LinkButton>
                  <LinkButton
                    aria-label="account"
                    onClick={() => navigateByName('compDatabase')}
                    isActive={route === 'compDatabase'}
                  >
                    Comp Database
                  </LinkButton>
                </>
              )}
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: '4px' }}>
          {isLoggedIn && userRegistration && route !== 'completeProfile' && (
            <>
              {isAdmin && (
                <LinkButton aria-label="admin" sx={{ color: '#FFFFFF' }} onClick={() => navigateByName('createUser')}>
                  Admin
                </LinkButton>
              )}
            </>
          )}
          {isLoggedOut && !userRegistration && <GoogleLogin />}
          {canEditDiligence && mdScreen && (
            <IconButton aria-label="edit diligence" sx={{ color: '#FFFFFF' }} onClick={() => onEditDiligenceClick()}>
              <Edit />
            </IconButton>
          )}
          {canEditDiligence && !mdScreen && (
            <Button
              variant="outlined"
              sx={{ lineHeight: '24px', color: '#FFFFFF' }}
              aria-label="edit diligence"
              onClick={() => onEditDiligenceClick()}
            >
              Edit Diligence
            </Button>
          )}
          {canShareDiligence && mdScreen && (
            <IconButton aria-label="share diligence" sx={{ color: '#FFFFFF' }} onClick={() => onShareDiligenceClick()}>
              <Share />
            </IconButton>
          )}
          {canShareDiligence && !mdScreen && (
            <Button
              variant="outlined"
              sx={{ lineHeight: '24px', color: '#FFFFFF' }}
              aria-label="share diligence"
              onClick={() => onShareDiligenceClick()}
            >
              Share
            </Button>
          )}
          {isLoggedIn && mdScreen && (
            <>
              <LinkButton variant="outlined" aria-label="logout" onClick={() => navigateByName('userProfile')}>
                {currentUser?.photoUrl ? (
                  <Avatar
                    sx={{ width: 36, height: 36 }}
                    alt="user icon"
                    src={currentUser.photoUrl}
                    imgProps={{ referrerPolicy: 'no-referrer' }}
                  />
                ) : (
                  <PersonIcon sx={{ color: '#FFFFFF' }} />
                )}
              </LinkButton>
              <IconButton aria-label="logout" sx={{ color: '#FFFFFF' }} onClick={logout}>
                <ExitToApp />
              </IconButton>
            </>
          )}
          {isLoggedIn && !mdScreen && (
            <>
              <LinkButton variant="outlined" aria-label="account" sx={{ marginRight: 0 }} onClick={handleMenu}>
                {currentUser?.photoUrl ? (
                  <Avatar
                    sx={{ width: 36, height: 36 }}
                    alt="user icon"
                    src={currentUser.photoUrl}
                    imgProps={{ referrerPolicy: 'no-referrer' }}
                  />
                ) : (
                  'Account'
                )}
              </LinkButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem onClick={() => navigateByName('userProfile')}>Profile</MenuItem>
                <MenuItem onClick={logout}>Log Out</MenuItem>
              </Menu>
            </>
          )}
          {isLoggedOut && userRegistration && (
            <>
              <LinkButton aria-label="log in" sx={{ color: '#FFFFFF' }} onClick={() => navigateByName('login')}>
                Log In
              </LinkButton>
              <LinkButton
                variant="outlined"
                aria-label="admin"
                sx={{ color: '#FFFFFF' }}
                onClick={() => navigateByName('register')}
              >
                Create Account
              </LinkButton>
            </>
          )}
        </div>
      </Toolbar>
    </AppBar>
  );
});

export { Header };
